import React from 'react';
import SEOHead, { faqSchema } from '@components/marketing/SEOHead';
import PageHead from '@components/marketing/PageHead';
import CtaBand from '@components/marketing/CtaBand';
import { TABLE } from './content';

const RECORDS = [
  ['Most runs', 'Box cricket, by city and by category, with balls faced and the boundary count behind each total.'],
  ['Most wickets', 'With overs bowled and economy, so a haul in one spell reads differently from one across a tournament.'],
  ['Most sixes', 'Counted from the ball-by-ball record rather than entered separately, so it cannot disagree with the scorecard.'],
  ['Top scorers', 'Football goals credited to the player as each one is entered.'],
  ['Biggest wins', 'By margin, across the sports where a margin means something.'],
  ['Straight-game wins', 'Racket sports, where winning without dropping a game is the thing worth knowing.'],
];

const FAQS = [
  ['How are group tables calculated?',
   'Three points for a win, one for a draw, none for a loss. A level score in a group is a draw regardless of the sport — the sport’s tie-break only applies in a knockout, where somebody has to go through.'],
  ['What separates teams level on points?',
   'Goal difference, then goals scored, in the sports where those apply. Where the format needs something else, the order is configurable.'],
  ['Are the record boards updated live?',
   'They follow the results, so a board reflects everything scored up to that moment, including matches still in progress where the sport reports as it goes.'],
  ['Can the public see standings without logging in?',
   'Yes. Tables and record boards sit on the association’s public site, filterable by sport, city and category, with no account needed.'],
];

const StandingsPage = () => (
  <>
    <SEOHead
      title="Standings and record boards"
      description="Group tables at three points a win, knockout progression, and record boards for most runs, most wickets, most sixes and top scorers — by sport, city and category."
      schema={faqSchema(FAQS)}
    />

    <PageHead title="Tables that build themselves" image="/images/sportstms/podium.jpg">
          <p>
            Nobody should be totalling a group by hand on the last day. Results go in once at
            the court, and everything downstream — the table, the qualifiers, the record
            boards — follows from them.
          </p>
    </PageHead>

    <section className="stms-sec stms-sec--ink">
      <div className="stms-wrap stms-split">
        <div className="stms-split__say">
          <h2>Three points a win, one a draw</h2>
          <p>
            A group table for every city and every category, with played, won, drawn, lost,
            for, against and difference. The rows that qualify are marked, so the question
            everyone asks on the last morning is answered on the screen.
          </p>
          <p>
            A level score in a group is a draw. A knockout that cannot end level goes to the
            sport’s own tie-break, and that tie-break is recorded with the result.
          </p>
        </div>
        <div className="stms-split__do">
          <div className="stms-table">
            <div className="stms-table__top">Group A — Men</div>
            <table>
              <thead>
                <tr>
                  <th scope="col" className="stms-table__pos">#</th>
                  <th scope="col">Team</th>
                  <th scope="col">P</th><th scope="col">W</th><th scope="col">D</th>
                  <th scope="col">L</th><th scope="col">GF</th><th scope="col">GA</th>
                  <th scope="col">GD</th><th scope="col">Pts</th>
                </tr>
              </thead>
              <tbody>
                {TABLE.map((r) => (
                  <tr key={r.pos} className={r.pos <= 2 ? 'is-through' : ''}>
                    <td className="stms-table__pos stms-num">{r.pos}</td>
                    <th scope="row">{r.team}</th>
                    <td className="stms-num">{r.p}</td><td className="stms-num">{r.w}</td>
                    <td className="stms-num">{r.d}</td><td className="stms-num">{r.l}</td>
                    <td className="stms-num">{r.gf}</td><td className="stms-num">{r.ga}</td>
                    <td className="stms-num">{r.gd}</td>
                    <td className="stms-num stms-table__pts">{r.pts}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="stms-table__foot">Top two go through to the semi-finals.</p>
          </div>
        </div>
      </div>
    </section>

    <section className="stms-sec">
      <div className="stms-wrap">
        <div className="stms-lede">
          <h2>The boards behind the tables</h2>
          <p>Every record is derived from the same ball-by-ball and point-by-point data the scorecards are built from, so the two can never disagree.</p>
        </div>
        <dl className="stms-caps">
          {RECORDS.map(([term, def]) => (
            <div key={term}><dt>{term}</dt><dd>{def}</dd></div>
          ))}
        </dl>
      </div>
    </section>

    <section className="stms-sec stms-sec--paper">
      <div className="stms-wrap">
        <div className="stms-lede"><h2>Common questions</h2></div>
        <dl className="stms-faq">
          {FAQS.map(([q, a]) => (
            <div key={q}><dt>{q}</dt><dd>{a}</dd></div>
          ))}
        </dl>
      </div>
    </section>

    <CtaBand title="See your own table" image="/images/sportstms/volley.jpg" action="Book a demo">
      <p>Give us a format and we will show the standings it would produce.</p>
    </CtaBand>
  </>
);

export default StandingsPage;
